import { useEffect, useState, useCallback } from 'react'
import { RefreshCw, RotateCw, Trash2, CheckSquare, Square, XCircle } from 'lucide-react'
import { api } from '../../api/client'
import type { QueueMessage, QueuePage as QueuePageData } from '../../api/types'
import { EmptyState } from '../../components/EmptyState'
import { PageHeader } from '../../components/PageHeader'
import { useI18n } from '../../i18n'

const statuses = ['', 'queued', 'sending', 'sent', 'failed']

export function QueuePage() {
  const { t } = useI18n()
  const [items, setItems] = useState<QueueMessage[]>([])
  const [total, setTotal] = useState(0)
  const [page, setPage] = useState(1)
  const [status, setStatus] = useState('')
  const [selected, setSelected] = useState<Set<string>>(new Set())
  const [error, setError] = useState('')
  const size = 20

  const load = useCallback(() => {
    const params = `?page=${page}&size=${size}${status ? `&status=${status}` : ''}`
    api.get<QueuePageData>(`/admin/queue${params}`)
      .then((data) => { setItems(data.items || []); setTotal(data.total); setSelected(new Set()) })
      .catch((e: Error) => setError(e.message))
  }, [page, status])

  useEffect(() => { load() }, [load])

  const totalPages = Math.max(1, Math.ceil(total / size))

  async function retry(id: string) {
    try { await api.post(`/admin/queue/${id}/retry`); load() }
    catch (e) { setError(e instanceof Error ? e.message : 'error') }
  }

  async function remove(id: string) {
    if (!confirm('Delete this queued message?')) return
    try { await api.delete(`/admin/queue/${id}`); load() }
    catch (e) { setError(e instanceof Error ? e.message : 'error') }
  }

  async function batchDelete() {
    if (selected.size === 0) return
    if (!confirm(`Delete ${selected.size} messages?`)) return
    try { await api.post('/admin/queue/batch-delete', { ids: [...selected] }); load() }
    catch (e) { setError(e instanceof Error ? e.message : 'error') }
  }

  async function clearByStatus() {
    const target = status || 'all'
    if (!confirm(`Clear all ${target} messages from queue?`)) return
    try { await api.post('/admin/queue/clear', { status: status || undefined }); load() }
    catch (e) { setError(e instanceof Error ? e.message : 'error') }
  }

  function toggleSelect(id: string) {
    setSelected((prev) => { const next = new Set(prev); next.has(id) ? next.delete(id) : next.add(id); return next })
  }
  function toggleAll() {
    if (selected.size === items.length) setSelected(new Set())
    else setSelected(new Set(items.map((i) => i.id)))
  }

  return <>
    <PageHeader title="admin.queue" action={
      <div className="flex items-center gap-2">
        {selected.size > 0 && <button onClick={batchDelete} className="flex h-10 items-center gap-2 rounded-md bg-destructive px-4 text-sm font-medium text-destructive-foreground"><Trash2 size={16}/>{selected.size}</button>}
        <button onClick={clearByStatus} className="flex h-10 items-center gap-2 rounded-md border bg-card px-4 text-sm font-medium"><XCircle size={16}/>{t('common.delete')}</button>
        <button onClick={load} className="flex h-10 items-center gap-2 rounded-md border bg-card px-4 text-sm font-medium"><RefreshCw size={16}/>{t('common.refresh')}</button>
      </div>
    } />

    {/* Status tabs */}
    <div className="flex gap-2 mb-4 flex-wrap">
      {statuses.map((s) => (
        <button key={s} onClick={() => { setStatus(s); setPage(1) }}
          className={`px-3 py-1.5 rounded-md text-sm font-medium border ${status === s ? 'bg-primary text-primary-foreground' : 'bg-card'}`}>
          {s === '' ? t('common.all') : s}
        </button>
      ))}
    </div>

    {error && <p className="mb-4 text-sm text-destructive">{error}</p>}

    {items.length === 0 ? <EmptyState title="admin.emptyQueue" /> : <>
      <div className="panel overflow-x-auto">
        <table className="w-full text-left text-sm">
          <thead className="border-b text-muted-foreground">
            <tr>
              <th className="p-4 w-10"><button onClick={toggleAll}>{selected.size === items.length ? <CheckSquare size={16}/> : <Square size={16}/>}</button></th>
              <th className="p-4">{t('admin.sender')}</th>
              <th className="p-4">{t('admin.recipient')}</th>
              <th className="p-4">{t('admin.subject')}</th>
              <th className="p-4">{t('common.status')}</th>
              <th className="p-4">{t('common.actions')}</th>
            </tr>
          </thead>
          <tbody>
            {items.map((item) => (
              <tr className="border-b last:border-0" key={item.id}>
                <td className="p-4"><button onClick={() => toggleSelect(item.id)}>{selected.has(item.id) ? <CheckSquare size={16}/> : <Square size={16}/>}</button></td>
                <td className="p-4">{item.sender}</td>
                <td className="p-4">{item.recipient}</td>
                <td className="p-4 max-w-[200px] truncate">{item.subject}</td>
                <td className="p-4"><span className={`px-2 py-0.5 rounded text-xs font-medium ${item.status === 'sent' ? 'bg-emerald-100 text-emerald-700' : item.status === 'failed' ? 'bg-red-100 text-red-700' : 'bg-amber-100 text-amber-700'}`}>{item.status}</span> · {item.attempts}</td>
                <td className="p-4">
                  <button onClick={() => retry(item.id)} className="mr-2 rounded border p-2" aria-label="Retry"><RotateCw size={15}/></button>
                  <button onClick={() => remove(item.id)} className="rounded border p-2 text-destructive" aria-label="Delete"><Trash2 size={15}/></button>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      {/* Pagination */}
      {totalPages > 1 && (
        <div className="flex items-center justify-between mt-4 text-sm">
          <span className="text-muted-foreground">{total} {t('admin.queuedMessages')}</span>
          <div className="flex items-center gap-1">
            <button disabled={page <= 1} onClick={() => setPage(page - 1)} className="px-3 py-1.5 rounded border bg-card disabled:opacity-50">←</button>
            <span className="px-3 py-1.5">{page} / {totalPages}</span>
            <button disabled={page >= totalPages} onClick={() => setPage(page + 1)} className="px-3 py-1.5 rounded border bg-card disabled:opacity-50">→</button>
          </div>
        </div>
      )}
    </>}
  </>
}
